import React, {useState} from "react"
// MUI
import {Box, Typography, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Button, Stack} from "@mui/material"
import {MoreVert, EditOutlined, DeleteOutlined, Add} from "@mui/icons-material"
// Layout
import {Badge} from "@/components/Layout"
import MoreChip, {MAX_VISIBLE_CHIPS} from "@/components/UI/MoreChip/MoreChip"
import {PropagatedFiltersChip, PropagatedFiltersIcon} from "@/components/UI/PropagatedFilters/PropagatedFilters"
import {FilterPreset} from "@/utils/filterPresets"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {formatJSIso8601ToHumanString} from "@/utils/timeFunctions"

type BrandCardProps = {
    brand: any
    lockEdition?: boolean
    onEditBrand: (brand: any) => void
    onDeleteBrand: (brand: any) => void
    onEditRange: (range: any) => void
    onDeleteRange: (range: any) => void
    onAddRange: (brand: any) => void
    // Filters propagation synthesis: the brand's own preset, and the presets
    // of the ranges that override it (keyed by range id). Inheriting ranges
    // are not listed — the brand chip already covers them.
    propagatedFilters?: FilterPreset[]
    rangePropagatedFilters?: Record<string, FilterPreset[]>
    t: (k: string, o?: any) => string
}

export default function BrandCard(props: BrandCardProps) {
    const {
        brand, lockEdition, onEditBrand, onDeleteBrand, onEditRange, onDeleteRange, onAddRange,
        propagatedFilters, rangePropagatedFilters, t,
    } = props
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

    const ranges = brand.ranges ?? []
    // Range chips collapse their tail into a MoreChip past the cap.
    const visibleRanges = ranges.slice(0, MAX_VISIBLE_CHIPS)
    const hiddenRanges = ranges.slice(MAX_VISIBLE_CHIPS)
    const attributed = brand.attributed_org_name || brand.attributed_user_email

    return (
        <Box
            sx={{
                backgroundColor: tk.color.surface,
                border: `1px solid ${tk.color.border}`,
                borderRadius: tk.radius.card,
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                transition: "all .2s",
                "&:hover": {boxShadow: tk.shadow.md, borderColor: tk.color.borderStrong},
            }}
        >
            {/* Header — brand name + ranges badge (the letter-avatar was
                dropped 2026-07: name-only reads cleaner) */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5}}>
                <Typography
                    sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {brand.name}
                </Typography>
                <Box sx={{flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75}}>
                    <PropagatedFiltersChip presets={propagatedFilters} />
                    <Badge tone={"green"}>
                        {t("n_ranges", {n: ranges.length, defaultValue: `${ranges.length} ranges`})}
                    </Badge>
                </Box>
            </Box>

            {/* Meta */}
            <Typography sx={{fontSize: "12.5px", color: tk.color.textTertiary}}>
                {t("created_at")} {formatJSIso8601ToHumanString(brand.created_at)}
                {attributed ? ` · ${attributed}` : ""}
            </Typography>

            {brand.description && (
                <Typography sx={{fontSize: "13px", color: tk.color.textSecondary, mt: 1}}>
                    {brand.description}
                </Typography>
            )}

            {/* Ranges */}
            <Box sx={{mt: 1.75, pt: 1.75, borderTop: `1px solid ${tk.color.borderLight}`}}>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center"}}>
                    {ranges.length === 0 && (
                        <Typography sx={{fontSize: "12.5px", color: tk.color.textTertiary, fontStyle: "italic"}}>
                            {t("no_ranges", {defaultValue: "No ranges yet"})}
                        </Typography>
                    )}
                    {visibleRanges.map((range: any) => (
                        <Chip
                            key={range.id}
                            label={range.name}
                            size={"small"}
                            icon={
                                rangePropagatedFilters?.[range.id]
                                    ? <PropagatedFiltersIcon presets={rangePropagatedFilters[range.id]} brandName={brand.name} />
                                    : undefined
                            }
                            onClick={lockEdition ? undefined : () => onEditRange(range)}
                            onDelete={lockEdition ? undefined : () => onDeleteRange(range)}
                            sx={{
                                height: 24,
                                borderRadius: tk.radius.pill,
                                backgroundColor: tk.color.bg,
                                border: `1px solid ${tk.color.borderLight}`,
                                fontSize: "11.5px",
                                fontWeight: 500,
                                color: tk.color.textSecondary,
                                "& .MuiChip-deleteIcon": {fontSize: 14, color: tk.color.textTertiary, "&:hover": {color: tk.color.red}},
                                "&:hover": {borderColor: tk.color.borderStrong},
                            }}
                        />
                    ))}
                    <MoreChip labels={hiddenRanges.map((r: any) => r.name)} />
                </Box>
            </Box>

            {/* Actions — same grammar as AppCard: labeled buttons in the
                footer, kebab bottom-right with the destructive action. */}
            <Stack direction={"row"} spacing={1} alignItems={"center"} sx={{mt: 1.75, pt: 1.75, borderTop: `1px solid ${tk.color.borderLight}`}}>
                <Button
                    size={"small"} disabled={lockEdition} onClick={() => onEditBrand(brand)}
                    startIcon={<EditOutlined sx={{fontSize: 15}} />}
                    sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 600, color: tk.color.textPrimary, border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm, px: 1.5, "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong}}}
                >
                    {t("common:edit")}
                </Button>
                <Button
                    size={"small"}
                    disabled={lockEdition}
                    onClick={() => onAddRange(brand)}
                    startIcon={<Add sx={{fontSize: 15}} />}
                    sx={{
                        textTransform: "none",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: tk.color.greenDark,
                        backgroundColor: tk.color.green50,
                        border: `1px solid ${tk.color.green100}`,
                        borderRadius: tk.radius.sm,
                        px: 1.5,
                        "&:hover": {backgroundColor: tk.color.green100},
                    }}
                >
                    {t("range_create")}
                </Button>
                <Box sx={{flex: 1}} />
                <IconButton
                    size={"small"}
                    disabled={lockEdition}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    aria-label={t("common:more_actions", {defaultValue: "More actions"})}
                    sx={{color: tk.color.textTertiary, "&:hover": {backgroundColor: tk.color.bg}}}
                >
                    <MoreVert fontSize={"small"} />
                </IconButton>
            </Stack>

            {/* Brand actions menu — destructive only, like AppCard */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                MenuListProps={{dense: true}}
            >
                <MenuItem onClick={() => {onDeleteBrand(brand); setMenuAnchor(null)}} sx={{color: tk.color.red}}>
                    <ListItemIcon><DeleteOutlined fontSize={"small"} sx={{color: tk.color.red}} /></ListItemIcon>
                    <ListItemText>{t("common:delete")}</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    )
}
